import { useCallback } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';

import { fromId as getCourseMaterialsById } from 'pages/open-course/common/data/courseMaterials';

import { getAssessmentRoutePath } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

type Props = {
  courseSlug: string;
  courseId: string;
  onError: (error: unknown) => void;
};

type CourseMaterialItem = {
  id: string;
  slug: string;
  contentSummary: {
    typeName: string;
  };
};

type CourseMaterialLesson = {
  elements?: CourseMaterialItem[];
};

type CourseMaterialModule = {
  elements?: CourseMaterialLesson[];
};

type CourseMaterialsResponse = {
  elements?: CourseMaterialModule[];
};

const getCourseMaterialItems = (courseMaterialsResponse?: CourseMaterialsResponse) => {
  return (
    courseMaterialsResponse?.elements?.flatMap(
      (module) => module?.elements?.flatMap((lesson) => lesson?.elements || []) || []
    ) || []
  );
};

const useNavigateToSkillContent = ({ courseSlug, courseId, onError }: Props) => {
  const navigateToAssessment = useCallback<(assessmentId: string) => Promise<void>>(
    async (assessmentId) => {
      try {
        const courseMaterialsResponse = await getCourseMaterialsById(courseId);
        const courseItems = getCourseMaterialItems(courseMaterialsResponse as CourseMaterialsResponse);
        const assessmentItem = courseItems?.find((item: CourseMaterialItem) => item?.id === assessmentId);
        const itemType = getAssessmentRoutePath(assessmentItem?.contentSummary?.typeName);

        if (assessmentId && itemType) {
          setLocation(`/learn/${courseSlug}/${itemType}/${assessmentId}`);
        } else {
          setLocation(`/learn/${courseSlug}/home/welcome`);
        }
      } catch (error: unknown) {
        onError(error);
      }
    },
    [courseSlug, courseId, onError]
  );

  const navigateToCourse = useCallback<() => Promise<void>>(async () => {
    try {
      const courseMaterialsResponse = await getCourseMaterialsById(courseId);
      const firstCourseItem = getCourseMaterialItems(courseMaterialsResponse as CourseMaterialsResponse)?.[0];
      const itemSlug = firstCourseItem?.slug;
      const itemId = firstCourseItem?.id;
      const itemType = firstCourseItem?.contentSummary?.typeName;

      if (itemSlug && itemId && itemType) {
        setLocation(`/learn/${courseSlug}/${itemType}/${itemId}/${itemSlug}`);
      } else {
        setLocation(`/learn/${courseSlug}/home/welcome`);
      }
    } catch (error: unknown) {
      onError(error);
    }
  }, [courseSlug, courseId, onError]);

  return { navigateToAssessment, navigateToCourse };
};

export default useNavigateToSkillContent;
